import React, { useEffect, useState } from "react";
import axios from "axios"; // Or use fetch if you prefer
import HomeLayout from "../layouts/home";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { CreditCard, RotateCcw, Star, Terminal, Trash2, Edit2 } from "lucide-react";
import { Skeleton } from "../components/ui/skeleton"; // Assuming you have the skeleton component
import { Button } from "../components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Spinner } from "../components/ui/spinner";
import Cookie, { JWT_COOKIE_NAME } from "../lib/cookie";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { API_BASE_URL } from "../lib/constants";
import { Badge } from "../components/ui/badge";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../contexts/I18nProvider";

const getInitials = (name) => {
  // Split the name by space, take the first letter of each word, and return them
  const nameParts = name.split(" ");
  const initials = nameParts
    .map((part) => part.charAt(0).toUpperCase())
    .join(""); // Join the initials together
  return initials;
};

function Profile() {
  const { t, i18n, ready } = useTranslation();
  
  // Debug i18n state
  useEffect(() => {
    console.log("=== I18N DEBUG ===");
    console.log("i18n ready:", ready);
    console.log("i18n initialized:", i18n?.isInitialized);
    console.log("Current language:", i18n?.language);
    console.log("Available languages:", i18n?.languages);
    
    // Test basic translations
    const testTranslations = [
      'profile.title',
      'profile.sections.overview', 
      'profile.quickStats',
      'profile.plan.free'
    ];
    
    testTranslations.forEach(key => {
      const result = t(key);
      console.log(`${key} -> ${result} ${result === key ? '(MISSING!)' : '(OK)'}`);
    });
    
    // Check raw resources
    if (i18n?.language) {
      const bundle = i18n.getResourceBundle(i18n.language);
      console.log("Resource bundle:", bundle);
      console.log("Profile section:", bundle?.profile);
    }
    console.log("===================");
  }, [t, i18n, ready]);
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasNoPaymentDetails, setHasNoPaymentDetails] = useState(null);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(API_BASE_URL + "/api/user/profile", {
        headers: {
          Authorization: `Bearer ${Cookie.get(JWT_COOKIE_NAME)}`,
        },
      });
      setProfile(response.data);
      setLoading(false);
    } catch (err) {
      if (err.response.data.type === "payment") {
        setError(err.response.data.error);
        setHasNoPaymentDetails(true);
      } else {
        setError("Failed to load profile");
      }
      setLoading(false);
    }
  };
  const [user, setUser] = useState();
  const [deletingAccount, setDeletingAccount] = useState(false);

  // Fetch profile data when the component mounts
  useEffect(() => {
    if (localStorage) {
      setUser(JSON.parse(localStorage.getItem("user") || "{}"));
    }
    fetchProfile();
  }, []);

  const handleDeleteAccount = () => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete your account? This action cannot be undone. All your data will be permanently removed."
    );

    if (!confirmDelete) {
      return;
    }

    setDeletingAccount(true);
    axios
      .delete(API_BASE_URL + "/api/user/delete-account", {
        headers: {
          Authorization: `Bearer ${Cookie.get(JWT_COOKIE_NAME)}`,
        },
      })
      .then(() => {
        // Clear user data from localStorage
        localStorage.removeItem("user");
        Cookie.remove(JWT_COOKIE_NAME);
        // Redirect to home page
        window.location.href = "/";
      })
      .catch((err) => {
        console.error("Error deleting account:", err);
        alert("Failed to delete account. Please try again.");
        setDeletingAccount(false);
      });
  };

  const handleEditProfile = () => {
    // Navigate to edit profile page or open modal
    navigate("/profile/edit");
  };

  const navigate = useNavigate();
  return (
    <HomeLayout>
      <div className="flex flex-col gap-8">
        {/* Header */}
        <div className="rounded-2xl border border-border/40 bg-white/95 px-8 py-10 shadow-sm">
          <h1 className="text-4xl font-bold text-primary mb-6">{t('pages.profile.title')}</h1>
          <div className="flex items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <Avatar className="w-16 h-16">
                <AvatarImage size={64} src="/shadcn.jpeg" />
                <AvatarFallback className="text-lg font-semibold">{getInitials(user?.name || "")}</AvatarFallback>
              </Avatar>
              <div className="flex flex-col gap-1">
                <p className="text-lg font-semibold text-text">{user?.name}</p>
                <p className="text-sm text-text-secondary">{user?.email}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {loading ? (
            <>
              <Skeleton className="w-full h-[200px] rounded-2xl" />
              <Skeleton className="w-full h-[200px] rounded-2xl" />
            </>
          ) : error ? (
            <Alert className="rounded-2xl border border-red-400/30 bg-red-50 dark:bg-red-900/20 lg:col-span-2">
              <Terminal className="h-4 w-4 text-red-600" />
              <AlertTitle className="text-red-700 dark:text-red-400">{t('pages.profile.headsUp')}</AlertTitle>
              <AlertDescription className="flex flex-col gap-3 text-red-600 dark:text-red-300 mt-2">
                {error}
                {hasNoPaymentDetails ? (
                  <Button
                    onClick={() => navigate("/pricing")}
                    className="mt-2 w-fit bg-primary hover:bg-primary-dark text-white font-semibold"
                    disabled={loading}
                  >
                    {loading ? <Spinner /> : null} <CreditCard className="w-4 h-4" /> {t('pages.profile.setupPayment')}
                  </Button>
                ) : (
                  <Button
                    onClick={fetchProfile}
                    className="mt-2 w-fit bg-primary hover:bg-primary-dark text-white font-semibold"
                    disabled={loading}
                  >
                    {loading ? <Spinner /> : null} {t('pages.profile.retry')} <RotateCcw size={14} />
                  </Button>
                )}
              </AlertDescription>
            </Alert>
          ) : (
            <>
              <Card className="bg-white/95 border border-border/40 rounded-2xl shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-primary">
                    <Star size={20} /> {t('pages.profile.currentPlan')}
                  </CardTitle>
                  <CardDescription className="text-text-secondary">
                    {t('pages.profile.currentPlanDesc')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {loading ? (
                    <div className="flex flex-col gap-4">
                      <Skeleton className="w-full h-10 rounded-lg" />
                      <Skeleton className="w-full h-4 rounded-lg" />
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <Badge className="bg-primary text-white">{profile?.tier}</Badge>
                        <Badge variant="outline">{profile?.frequency}</Badge>
                      </div>
                      <div className="space-y-2 text-sm">
                        <p className="text-text"><strong>Paid at:</strong> {new Date(profile?.paid_at).toDateString()}</p>
                        <p className="text-text"><strong>Expires on:</strong> {new Date(profile?.subscriptionExpiresAt).toDateString()}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-white/95 border border-border/40 rounded-2xl shadow-sm">
                <CardHeader>
                  <CardTitle className="text-primary">Quick Actions</CardTitle>
                  <CardDescription className="text-text-secondary">
                    Manage your account settings
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col gap-3">
                  <Button
                    onClick={handleEditProfile}
                    variant="default"
                    className="w-full bg-primary hover:bg-primary-dark text-white font-semibold"
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit Profile
                  </Button>
                  <Button
                    onClick={() => navigate("/pricing")}
                    variant="default"
                    className="w-full bg-primary hover:bg-primary-dark text-white font-semibold"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    View Subscription Plans
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full border border-border/60 hover:bg-accent/20"
                  >
                    Download Data
                  </Button>
                  <Button
                    onClick={handleDeleteAccount}
                    disabled={deletingAccount}
                    variant="destructive"
                    className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold mt-4"
                  >
                    {deletingAccount ? (
                      <>
                        <Spinner className="w-4 h-4 mr-2" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Account
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </HomeLayout>
  );
}

export default Profile;
